<script setup>
import { onMounted, ref, watch } from "vue";
import axios from "axios";
import Button from "primevue/button";
import WizardButton from "../../../components/ui-components/WizardButton.vue";
import Select from "primevue/select";
import Avatar from "primevue/avatar";
import InfoBlock from "../../../components/ui-components/InfoBlock.vue";
import { usePage } from "@inertiajs/vue3";
import CalendarListView from "../common/CalendarListView.vue";

const page = usePage();
const connectedProviders = page.props.connectedProviders ?? [];

const calendars = ref([]);
const model = defineModel();
const valid = defineModel('valid');

const loading = ref(false);
const error = ref(false);

const fetchCalendars = (refresh = false) => {
    if (!connectedProviders.length) return;

    loading.value = true;
    error.value = false;

    const params = refresh ? '?refresh=true' : '';

    Promise.all(
        connectedProviders.map(provider =>
            axios.get(`/app/forms/available_calendars/${provider}${params}`)
                .then(r => r.data ?? [])
                .catch(() => null)
        )
    ).then(results => {
        const hasError = results.some(r => r === null);
        const merged = results.filter(r => Array.isArray(r)).flat();

        calendars.value = merged;
        error.value = hasError && !merged.length;
    }).finally(() => {
        loading.value = false;
    });
};

watch(model, (now) => {
    if (now.useOneCalendar === true || now.useMultipleWorkers === false) {
        valid.value = now.calendarId !== '';
    } else {
        valid.value = Object.values(now.workersCalendars).length === now.workers.length;
    }
}, { immediate: true, deep: true });

onMounted(() => fetchCalendars());
</script>

<template>
    <div class="flex flex-col gap-5 pb-6 pt-4">
        <div v-if="model.useMultipleWorkers" class="flex flex-col gap-4 items-start">
            <wizard-button @select="model.useOneCalendar = true" :active="model.useOneCalendar===true" icon="pi pi-calendar" :title="$t('forms.cal_conn_one_calendar')" :description="$t('forms.cal_conn_one_calendar_desc')" wide></wizard-button>
            <wizard-button @select="model.useOneCalendar = false" :active="model.useOneCalendar===false" icon="pi pi-calendar-plus" :title="$t('forms.cal_conn_multi_calendar')" :info="$t('forms.cal_conn_multi_calendar_info')" :description="$t('forms.cal_conn_multi_calendar_desc')" wide></wizard-button>
            <info-block>{{ $t('forms.cal_conn_warning') }}</info-block>
        </div>

        <!-- Hlavička se refresh tlačítkem -->
        <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500 dark:text-gray-400">
                <template v-if="loading">{{ $t('forms.cal_conn_loading') }}</template>
                <template v-else-if="error">{{ $t('forms.cal_conn_error') }}</template>
                <template v-else-if="!connectedProviders.length">{{ $t('forms.cal_conn_not_connected') }}</template>
                <template v-else>{{ calendars.length }} {{ calendars.length === 1 ? $t('forms.cal_conn_count_one') : calendars.length < 5 ? $t('forms.cal_conn_count_few') : $t('forms.cal_conn_count_many') }}</template>
            </p>
            <Button
                v-if="connectedProviders.length"
                icon="pi pi-refresh"
                size="small"
                severity="secondary"
                text
                :loading="loading"
                @click="fetchCalendars(true)"
                v-tooltip="$t('forms.calendar_refresh_tooltip')"
            />
        </div>

        <div class="flex w-full">
            <!-- Jeden kalendář -->
            <div v-if="!model.useMultipleWorkers || model.useOneCalendar" class="border rounded-xl p-3 bg-neutral-50 dark:border-gray-800 dark:bg-gray-900 w-full">
                <CalendarListView
                    v-model="model.calendarId"
                    :calendars="calendars"
                    :loading="loading"
                    :error="error"
                    :reconnect-providers="connectedProviders.length ? connectedProviders : ['google', 'microsoft']"
                    @refresh="fetchCalendars(true)"
                    @select="(cal) => { model.calendarTimezone = cal.timezone ?? null; valid = true }"
                />
            </div>

            <!-- Více kalendářů (per worker) -->
            <div class="border shadow-sm rounded-2xl bg-white dark:bg-black dark:border-neutral-700 mt-3 w-full pr-3" v-if="model.useMultipleWorkers && model.useOneCalendar === false">
                <div class="text-sm" :class="{ 'grid grid-cols-2': model.workers.length > 8 }">
                    <div v-for="(worker, i) in model.workers" :key="worker.id" class="flex max-md:flex-wrap dark:border-neutral-700" :class="{
                        'border m-1 rounded-xl': model.workers.length > 8,
                        'border-b': model.workers.length <= 8 && model.workers.length !== 1 && i !== model.workers.length - 1
                    }">
                        <div class="p-4 flex gap-3 md:w-1/4 items-center" :class="model.workers.length > 8 ? 'grow' : ''">
                            <Avatar :label="worker.initials"></Avatar>
                            <p class="font-bold">{{ worker.name }}</p>
                        </div>
                        <div class="flex flex-col max-md:p-4 max-md:pt-0 justify-center" :class="model.workers.length > 8 ? 'w-1/2' : 'grow'">
                            <Select
                                :options="calendars"
                                v-model="model.workersCalendars[`worker-${worker.id}`]"
                                option-label="name"
                                :loading="loading"
                                :placeholder="loading ? $t('forms.cal_conn_loading_short') : $t('forms.cal_conn_select_for', { worker: worker.genitive })"
                            >
                                <template #value="slotProps">
                                    <div v-if="slotProps.value" class="flex items-center gap-3">
                                        <i class="pi pi-calendar" :style="{ color: slotProps.value.color }"/>
                                        <div>{{ slotProps.value.name }}</div>
                                    </div>
                                    <span v-else class="opacity-50">{{ $t('forms.cal_conn_select_for', { worker: worker.genitive }) }}</span>
                                </template>
                                <template #option="slotProps">
                                    <div class="flex items-center gap-3">
                                        <i class="pi pi-calendar" :style="{ color: slotProps.option.color }"/>
                                        <div>{{ slotProps.option.name }}</div>
                                    </div>
                                </template>
                            </Select>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
